import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Box, Divider, HStack, Text } from '@bonusly/design-system';

import type { RecognitionDraftCard, RecognitionDraftVariant } from '../../../schemas/bizy-ui-blocks';
import { PillTab } from '../../tabs/pill-tab';
import { RECOGNITION_DRAFT_FOCUS_TAB_PROPS } from './lib/recognition-draft-focus-tab-props';
import { AmountPill, HashtagToken, RecipientHandle } from './lib/recognition-segments';
import { UiBlockActionBar } from './ui-block-action-bar';

/**
 * Recognition draft card — the user is writing a recognition and
 * Bizy is offering a drafted message.
 *
 * Visual shape (matches the live feed's recognition rendering — see
 * `src/components/bonus/text-preview.tsx` for the source-of-truth
 * styling we're re-using):
 *
 *   Frederick Allen: [+10] @judith.bartels Thank you for ... #hashtag
 *
 *   [📋]                                   [Send now] [Edit in givebox]
 *
 * Each segment uses the same color treatment a real bonus uses, so the
 * draft reads visually like the post the user is about to commit. No
 * divider line — vertical spacing separates the body from the action
 * row (the action bar handles the `mt`).
 *
 * BIZY-424: when the payload carries multiple `variants` (each
 * foregrounding a different facet of the recognition) the card shows a
 * tab per focus; selecting one swaps the body + actions client-side (no
 * round-trip), so the user picks an angle instead of looping through
 * "make it warmer" tweaks. The shared giver/recipient header stays put.
 * With zero or one variant it renders exactly as it always has, reading
 * from the top-level payload fields.
 */
export function RecognitionDraftCardView({
	block,
	fontSize = 'sm',
	isReadOnly = false,
}: {
	block: RecognitionDraftCard;
	fontSize?: 'sm' | 'md';
	isReadOnly?: boolean;
}) {
	const { t } = useTranslation('admin_bizy');
	const { giver, recipient, amount, message, hashtags, actions, variants } = block.payload;
	const [activeIndex, setActiveIndex] = useState(0);

	// The top-level fields already mirror the first variant, so a card
	// with one (or zero) variant renders identically to the pre-BIZY-424
	// shape. With several, the selected tab drives the body + actions
	// while giver/recipient stay shared.
	const hasTabs = (variants?.length ?? 0) > 1;
	const fallbackVariant: RecognitionDraftVariant = { focus: null, message, amount, hashtags, actions, qualityScore: null };
	// Clamp so a stale activeIndex (e.g. a streamed re-render that shortened
	// the list) can never desync the highlighted tab from the shown body.
	const safeIndex = Math.min(activeIndex, Math.max(0, (variants?.length ?? 1) - 1));
	const active = variants?.[safeIndex] ?? fallbackVariant;

	const recipients = Array.isArray(recipient) ? recipient : recipient ? [recipient] : [];
	const activeMessage = active.message;
	const activeAmount = active.amount;
	const activeActions = active.actions ?? [];
	const hashtagList = (active.hashtags ?? []).map((h) => h.replace(/^#/, ''));

	// Copy text reproduces what the user would paste into the give-box
	// input: amount + recipient mentions + body + hashtags. We hand
	// this to the action bar's left-side copy icon.
	const copyText = [
		activeAmount && activeAmount > 0 ? `+${activeAmount}` : null,
		...recipients.map((r) => (r.startsWith('@') ? r : `@${r}`)),
		activeMessage,
		...hashtagList.map((h) => `#${h}`),
	]
		.filter(Boolean)
		.join(' ');

	return (
		<Box my={2} px={3} py={3} bg="bg.surface" border="1px solid" borderColor="border.default" borderRadius="md">
			{hasTabs && (
				<>
					{/* BIZY-453: the focus tabs stay on a single horizontal row, but when the panel is too narrow to fit them the row scrolls horizontally (rather than clipping a pill mid-word). `flexShrink={0}` keeps each pill at full width so its label stays whole, and the native scrollbar is hidden to match the Bizy prompt-chips rail (see `src/components/prompt-chips/prompt-chips.tsx`). */}
					{/* `fontSize` + `lineHeight` on this wrapper keep tabs at sm/20px even when the parent passes `fontSize="md"` on fullscreen desktop. */}
					<Box overflowX="auto" mb={3} fontSize="sm" lineHeight="5" sx={{ scrollbarWidth: 'none', '&::-webkit-scrollbar': { display: 'none' } }}>
						<HStack role="tablist" spacing={2}>
							{variants?.map((v, i) => (
								<PillTab
									key={`${v.focus ?? 'focus'}-${i}`}
									role="tab"
									aria-selected={i === safeIndex}
									isActive={i === safeIndex}
									flexShrink={0}
									{...RECOGNITION_DRAFT_FOCUS_TAB_PROPS}
									onClick={() => setActiveIndex(i)}
								>
									{v.focus || t('uiBlocks.recognitionDraft.focusFallback', { index: i + 1 })}
								</PillTab>
							))}
						</HStack>
					</Box>
					{/* Full-width rule between the focus tabs and the draft body (matches the Figma). It is a sibling of the scroll Box above (never a child of it), so the scrollable pill row can't constrain it. BIZY-453: `width="auto"` overrides Chakra Divider's default `width:100%`, which—once the overflowing pill row stretches the card's layout width—resolves against the narrower visible width and leaves a gap on the right. With `auto`, the symmetric negative `mx={-3}` (cancelling the card's `px={3}`) drives both edges flush to the card border. */}
					<Divider borderColor="border.default" width="auto" mx={-3} mb={3} />
				</>
			)}
			{/* `fontSize="inherit"` on every segment + Text keeps the giver header, +amount pill, @recipient, body, and hashtags at the same visual size. */}
			<Text fontSize={fontSize} lineHeight="tall" whiteSpace="pre-wrap">
				{giver && (
					<Text as="span" fontSize="inherit" fontWeight="semibold">
						{giver}:{' '}
					</Text>
				)}
				{activeAmount != null && activeAmount > 0 && (
					<>
						<AmountPill amount={activeAmount} />{' '}
					</>
				)}
				{recipients.map((r, i) => (
					<span key={`${r}-${i}`}>
						<RecipientHandle username={r} />{' '}
					</span>
				))}
				{activeMessage}
				{hashtagList.length > 0 && (
					<>
						{' '}
						{hashtagList.map((h, i) => (
							<span key={`${h}-${i}`}>
								<HashtagToken hashtag={h} />
								{i < hashtagList.length - 1 ? ' ' : ''}
							</span>
						))}
					</>
				)}
			</Text>
			<UiBlockActionBar actions={activeActions} copyText={copyText} areActionsDisabled={isReadOnly} />
		</Box>
	);
}
